import React, { useState, useEffect, useRef } from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  CubeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid
} from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

// Tellency ERP tier definitions — source of truth
const userTiers = {
  starter: {
    name: 'Starter',
    price: 79,
    color: 'blue',
    description: 'View data, basic reports, mobile app access',
    maxUsers: 10
  },
  professional: {
    name: 'Professional',
    price: 119,
    color: 'purple',
    description: 'Create, edit, delete records, workflows, API access',
    maxUsers: 50
  },
  enterprise: {
    name: 'Enterprise',
    price: 0,
    color: 'amber',
    description: 'Full admin access, manage users, system settings',
    maxUsers: null
  }
};

// Map legacy tier names to new names
const normalizeTier = (tier) => {
  const mapping = { basic: 'starter', power: 'professional', admin: 'enterprise' };
  return mapping[tier] || tier || 'starter';
};

const getTierPrice = (tier) => {
  const normalized = normalizeTier(tier);
  return userTiers[normalized]?.price || 79;
};

const getTierInfo = (tier) => {
  const normalized = normalizeTier(tier);
  return userTiers[normalized] || userTiers.starter;
};

const ERPUserManagement = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [erpUsers, setErpUsers] = useState([]);
  const [ownedModules, setOwnedModules] = useState([]);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [filterTier, setFilterTier] = useState('all');
  const [assignUserId, setAssignUserId] = useState('');
  const [assignTier, setAssignTier] = useState('starter');
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [changingTier, setChangingTier] = useState(null); // userId being changed
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const [usersRes, erpUsersRes, modulesRes, enterpriseRes] = await Promise.all([
        // Request the full roster (high limit) and include the business owner so the
        // assign dropdown shows every tenant user, not just the first paginated page.
        axiosInstance.get('/api/business/users', { params: { limit: 1000, includeOwner: 'true' } }).catch(() => ({ data: { users: [] } })),
        axiosInstance.get('/api/business/erp-users/assignments').catch(() => ({ data: { assignments: [] } })),
        axiosInstance.get('/api/licenses/erp-modules/current').catch(() => ({ data: [] })),
        axiosInstance.get('/api/licenses/enterprise/current').catch(() => ({ data: null }))
      ]);

      logger.log('[ERP Assign] users API returned', usersRes.data?.users?.length, 'users; pagination:', usersRes.data?.pagination);
      setUsers(Array.isArray(usersRes.data?.users) ? usersRes.data.users : []);
      setErpUsers(Array.isArray(erpUsersRes.data?.assignments) ? erpUsersRes.data.assignments : []);
      setOwnedModules(Array.isArray(modulesRes.data) ? modulesRes.data : []);
      setIsEnterprise(enterpriseRes.data && enterpriseRes.data.status === 'active');
    } catch (error) {
      logger.error('Error fetching user data:', error);
      if (error.response && error.response.status !== 404) {
        showNotification('Error loading user information', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTotalMonthlyCost = () => {
    if (isEnterprise) return 0;
    return erpUsers.reduce((sum, user) => sum + getTierPrice(user.tier), 0);
  };

  const filteredErpUsers = erpUsers.filter(user => {
    const matchesSearch = user.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.userEmail?.toLowerCase().includes(searchQuery.toLowerCase());
    const normalizedUserTier = normalizeTier(user.tier);
    const matchesTier = filterTier === 'all' || normalizedUserTier === filterTier;
    return matchesSearch && matchesTier;
  });

  const filteredAvailableUsers = users.filter(user => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name.toLowerCase().includes(q) || (user.email || '').toLowerCase().includes(q);
  });

  const selectedUser = assignUserId ? users.find(u => (u._id || u.id) === assignUserId) : null;

  const getUserDisplayName = (user) => {
    return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email?.split('@')[0] || 'Unknown';
  };

  const getInitials = (name) => {
    if (!name || name === 'Unknown User') return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0]?.toUpperCase() || '?';
  };

  const handleOpenAssignPanel = () => {
    if (ownedModules.length === 0 && !isEnterprise) {
      showNotification('You need to purchase ERP modules before assigning users', 'error');
      return;
    }
    setAssignUserId('');
    setAssignTier('starter');
    setUserSearch('');
    setShowUserDropdown(false);
    setShowAssignPanel(true);
  };

  const assignErpAccess = async (formData) => {
    try {
      setProcessing(true);
      const response = await axiosInstance.post('/api/business/erp-users/assign', formData);
      if (response.data.success) {
        showNotification('Successfully assigned ERP access', 'success');
        setShowAssignPanel(false);
        fetchUserData();
      } else {
        showNotification(response.data.message || 'Failed to assign ERP access', 'error');
      }
    } catch (error) {
      logger.error('Error assigning ERP access:', error);
      showNotification('Error assigning ERP access', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleAssignSubmit = () => {
    if (!assignUserId) {
      showNotification('Please select a user', 'error');
      return;
    }
    const user = users.find(u => (u._id || u.id) === assignUserId);
    const fullName = getUserDisplayName(user);
    assignErpAccess({
      userAssignments: [{
        userId: assignUserId,
        userEmail: user?.email,
        userName: fullName
      }],
      tier: assignTier
    });
  };

  const updateUserTier = async (userId, newTier) => {
    try {
      setChangingTier(userId);
      const response = await axiosInstance.put(`/api/licenses/erp-users/${userId}/seat-type`, { seatType: newTier });
      if (response.data.success) {
        showNotification(`User tier updated to ${userTiers[newTier]?.name || newTier}`, 'success');
        fetchUserData();
      }
    } catch (error) {
      showNotification('Error updating user tier', 'error');
    } finally {
      setChangingTier(null);
    }
  };

  const removeErpAccess = async (userId) => {
    try {
      const response = await axiosInstance.delete(`/api/business/erp-users/remove/${userId}`);
      if (response.data.success) {
        showNotification('ERP access removed successfully', 'success');
        fetchUserData();
      }
    } catch (error) {
      showNotification('Error removing ERP access', 'error');
    }
  };

  // Tier counts
  const tierCounts = erpUsers.reduce((acc, u) => {
    const t = normalizeTier(u.tier);
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] flex flex-col relative">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <UserGroupIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">ERP User Access</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {erpUsers.length} {erpUsers.length === 1 ? 'user' : 'users'} &middot;{' '}
                {isEnterprise ? 'Included in enterprise' : `$${getTotalMonthlyCost()}/mo`}
              </p>
            </div>
          </div>
          <button
            onClick={handleOpenAssignPanel}
            className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
          >
            <PlusIcon className="w-3.5 h-3.5 mr-1.5" />
            Assign User
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">

        {/* Stat Cards */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
              <div className="flex items-center gap-2 mb-1">
                <UserGroupIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Users</span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{erpUsers.length}</p>
              <div className="flex items-center gap-2 mt-1">
                {Object.entries(tierCounts).map(([tier, count]) => (
                  <span key={tier} className="text-[10px] text-gray-400 dark:text-gray-500">
                    {userTiers[tier]?.name || tier}: {count}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
              <div className="flex items-center gap-2 mb-1">
                <CurrencyDollarIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monthly Cost</span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">
                {isEnterprise ? '$0' : `$${getTotalMonthlyCost()}`}
              </p>
              {isEnterprise && (
                <p className="text-[10px] text-green-600 dark:text-green-400 mt-1">Included in enterprise</p>
              )}
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
              <div className="flex items-center gap-2 mb-1">
                <CubeIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Active Modules</span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{ownedModules.length}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-3">
          <div className="flex items-center space-x-4">
            <div className="flex-1 max-w-sm relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div className="flex items-center gap-1.5">
              {['all', 'starter', 'professional', 'enterprise'].map(tier => (
                <button
                  key={tier}
                  onClick={() => setFilterTier(tier)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    filterTier === tier
                      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {tier === 'all' ? 'All' : userTiers[tier]?.name}
                  {tier !== 'all' && tierCounts[tier] ? ` (${tierCounts[tier]})` : ''}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="flex-1">
          {filteredErpUsers.length === 0 ? (
            <div className="text-center py-16">
              <UserGroupIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No ERP users found</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {searchQuery || filterTier !== 'all' ? 'Try adjusting your filters' : 'Start by assigning users to ERP modules'}
              </p>
              {!searchQuery && filterTier === 'all' && (
                <button
                  onClick={handleOpenAssignPanel}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5 mr-1" />
                  Assign Your First User
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.02]">
                  <th className="px-6 py-2.5 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                  <th className="px-6 py-2.5 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tier</th>
                  <th className="px-6 py-2.5 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cost</th>
                  <th className="px-6 py-2.5 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-2.5 text-right text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredErpUsers.map((user) => {
                  const tierInfo = getTierInfo(user.tier);
                  const tierPrice = getTierPrice(user.tier);
                  const normalizedTier = normalizeTier(user.tier);
                  const isChanging = changingTier === (user.id || user.userId);

                  const badgeColors = {
                    starter: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                    professional: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
                    enterprise: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  };

                  return (
                    <tr key={user.id || user.userId} className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-white">
                              {getInitials(user.userName)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {user.userName || 'Unknown User'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user.userEmail}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="relative inline-block">
                          <select
                            value={normalizedTier}
                            onChange={(e) => updateUserTier(user.id || user.userId, e.target.value)}
                            disabled={isChanging}
                            className={`appearance-none pr-6 pl-2 py-0.5 rounded-md text-xs font-medium border-0 cursor-pointer focus:ring-2 focus:ring-orange-500 ${badgeColors[normalizedTier]} ${isChanging ? 'opacity-50' : ''}`}
                          >
                            <option value="starter">Starter</option>
                            <option value="professional">Professional</option>
                            <option value="enterprise">Enterprise</option>
                          </select>
                          {isChanging ? (
                            <ArrowPathIcon className="w-3 h-3 absolute right-1 top-1/2 -translate-y-1/2 animate-spin pointer-events-none opacity-50" />
                          ) : (
                            <ChevronDownIcon className="w-3 h-3 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        {isEnterprise ? (
                          <span className="text-xs font-medium text-green-600 dark:text-green-400">Included</span>
                        ) : tierPrice > 0 ? (
                          <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">${tierPrice}/mo</span>
                        ) : (
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Custom</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          Active
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => removeErpAccess(user.id || user.userId)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                          title="Remove ERP access"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Right Side Panel — Assign User */}
      {showAssignPanel && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setShowAssignPanel(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md">
            <div className="w-full h-full bg-white dark:bg-[#1a1a1a] shadow-2xl flex flex-col">
              {/* Panel Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Assign ERP Access</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Grant a user access to ERP modules
                  </p>
                </div>
                <button
                  onClick={() => setShowAssignPanel(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Panel Body */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {/* User Selection with Search */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Select User
                    </label>
                    <div className="relative" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowUserDropdown(!showUserDropdown);
                          setUserSearch('');
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#222222] text-left focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        {selectedUser ? (
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-white">
                                {getUserDisplayName(selectedUser).charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {getUserDisplayName(selectedUser)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {selectedUser.email}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Choose a user...</span>
                        )}
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {showUserDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                            <div className="relative">
                              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                autoFocus
                                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-[#1a1a1a] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 outline-none"
                              />
                            </div>
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            {filteredAvailableUsers.length === 0 ? (
                              <div className="px-3 py-4 text-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {users.length === 0
                                    ? 'No users found in your account'
                                    : 'No users match your search'}
                                </p>
                              </div>
                            ) : (
                              filteredAvailableUsers.map((user) => {
                                const uid = user._id || user.id;
                                const isSelected = assignUserId === uid;
                                return (
                                  <button
                                    key={uid}
                                    type="button"
                                    onClick={() => {
                                      setAssignUserId(uid);
                                      setShowUserDropdown(false);
                                      setUserSearch('');
                                    }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors ${
                                      isSelected ? 'bg-orange-50 dark:bg-orange-900/10' : ''
                                    }`}
                                  >
                                    <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                        {getUserDisplayName(user).charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm text-gray-900 dark:text-white truncate">
                                        {getUserDisplayName(user)}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {user.email}
                                      </p>
                                    </div>
                                    {isSelected && (
                                      <CheckCircleIconSolid className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Access Tier */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Access Tier
                    </label>
                    <div className="space-y-2">
                      {Object.entries(userTiers).map(([key, tier]) => (
                        <label
                          key={key}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                            assignTier === key
                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10 dark:border-orange-500'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="tier"
                              value={key}
                              checked={assignTier === key}
                              onChange={() => setAssignTier(key)}
                              className="text-orange-600 focus:ring-orange-500"
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{tier.name}</p>
                                {key === 'professional' && (
                                  <span className="text-[9px] font-medium px-1 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                                    Popular
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{tier.description}</p>
                              {tier.maxUsers && (
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Up to {tier.maxUsers} users</p>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                            {isEnterprise ? (
                              <span className="text-green-600 dark:text-green-400 text-xs">Included</span>
                            ) : tier.price > 0 ? (
                              `$${tier.price}/mo`
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">Custom</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {isEnterprise && (
                    <div className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      Enterprise customers get unlimited ERP access at no additional cost.
                    </div>
                  )}

                  {/* Summary */}
                  {selectedUser && (
                    <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Summary</p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">User</span>
                          <span className="text-gray-900 dark:text-white font-medium">{getUserDisplayName(selectedUser)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Tier</span>
                          <span className="text-gray-900 dark:text-white font-medium">{userTiers[assignTier].name}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-1.5 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-400">Monthly cost</span>
                          <span className="text-gray-900 dark:text-white font-semibold tabular-nums">
                            {isEnterprise ? 'Included' : userTiers[assignTier].price > 0 ? `$${userTiers[assignTier].price}/mo` : 'Custom'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Panel Footer */}
              <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-4 flex gap-3">
                <button
                  onClick={() => setShowAssignPanel(false)}
                  className="flex-1 px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#222222] border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignSubmit}
                  disabled={processing || !assignUserId}
                  className="flex-1 px-3 py-2.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Assigning...' : 'Assign Access'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ERPUserManagement;
